import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export interface ConsultingStepItem {
  icon: string;
  color: string;
  step: string;
  title: string;
  desc: string;
}

export interface ConsultingProcessData {
  badge: string;
  titlePrefix: string;
  titleHighlight: string;
  subtitle: string;
  steps: ConsultingStepItem[];
  cta: {
    titlePrefix: string;
    titleHighlight: string;
    subtitle: string;
    ctaBook: string;
    ctaBookLink?: string;
    ctaServices: string;
    ctaServicesLink?: string;
  };
  disclaimer: {
    title: string;
    text: string;
  };
}

interface ConsultingCTAProps {
  data: ConsultingProcessData;
  consultingUrl: string;
  isEditing?: boolean;
  onChange?: (data: ConsultingProcessData) => void;
}

const ConsultingCTA: React.FC<ConsultingCTAProps> = ({
  data,
  consultingUrl,
  isEditing = false,
  onChange,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleStepChange = (index: number, field: 'title' | 'desc' | 'step', val: string) => {
    if (!onChange) return;
    const updated = data.steps.map((s, i) => (i === index ? { ...s, [field]: val } : s));
    onChange({ ...data, steps: updated });
  };

  const handleAddStep = () => {
    if (!onChange) return;
    const nextStepNum = String(data.steps.length + 1).padStart(2, '0');
    const newStep: ConsultingStepItem = {
      icon: 'ri-flag-line',
      color: '#8c52ff',
      step: nextStepNum,
      title: 'Nuevo Paso',
      desc: 'Detalle de la fase o acción a realizar en el proceso.',
    };
    onChange({ ...data, steps: [...data.steps, newStep] });
  };

  const handleDeleteStep = (index: number) => {
    if (!onChange) return;
    onChange({ ...data, steps: data.steps.filter((_, i) => i !== index) });
  };

  const effectiveBookLink = data.cta.ctaBookLink || consultingUrl;
  const effectiveServicesLink = data.cta.ctaServicesLink || '/services';

  return (
    <>
      {/* How It Works */}
      <section className="relative py-24 bg-[#030006] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(0,194,255,0.06)_0%,transparent_60%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-[#00c2ff]/10 border border-[#00c2ff]/30 rounded-full px-4 py-1.5 text-[#00c2ff] text-xs font-bold uppercase tracking-widest mb-5">
              <i className="ri-route-line" />
              {isEditing ? (
                <input
                  type="text"
                  value={data.badge}
                  onChange={(e) => onChange && onChange({ ...data, badge: e.target.value })}
                  className="bg-black/40 border border-[#00c2ff]/40 rounded px-2 text-[#00c2ff] text-xs font-bold uppercase tracking-widest focus:outline-none"
                />
              ) : (
                <span>{data.badge}</span>
              )}
            </div>

            {isEditing ? (
              <div className="max-w-xl mx-auto space-y-3 mb-6">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={data.titlePrefix}
                    onChange={(e) => onChange && onChange({ ...data, titlePrefix: e.target.value })}
                    className="flex-1 bg-black/40 border border-white/20 rounded px-3 py-2 text-white font-black text-2xl text-right focus:outline-none"
                  />
                  <input
                    type="text"
                    value={data.titleHighlight}
                    onChange={(e) => onChange && onChange({ ...data, titleHighlight: e.target.value })}
                    className="flex-1 bg-black/40 border border-[#00c2ff]/40 rounded px-3 py-2 text-cyan-300 font-black text-2xl text-left focus:outline-none"
                  />
                </div>
                <textarea
                  value={data.subtitle}
                  onChange={(e) => onChange && onChange({ ...data, subtitle: e.target.value })}
                  rows={2}
                  className="w-full bg-black/40 border border-white/10 rounded px-3 py-1.5 text-white/60 text-sm text-center focus:outline-none"
                />
              </div>
            ) : (
              <>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                  {data.titlePrefix}{' '}
                  <span className="bg-gradient-to-r from-[#00c2ff] to-[#8c52ff] bg-clip-text text-transparent">
                    {data.titleHighlight}
                  </span>
                </h2>
                <p className="text-white/50 text-base max-w-xl mx-auto">
                  {data.subtitle}
                </p>
              </>
            )}

            {/* Add Step button in edit mode */}
            {isEditing && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={handleAddStep}
                  className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 cursor-pointer"
                >
                  <i className="ri-add-circle-line" />
                  Añadir Paso al Proceso
                </button>
              </div>
            )}
          </div>

          <div
            ref={ref}
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {data.steps.map((s, i) => (
              <div
                key={i}
                className="relative rounded-2xl p-6 border border-white/8 group hover:border-white/20 transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                  transitionDelay: `${i * 80}ms`,
                }}
              >
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => handleDeleteStep(i)}
                    className="absolute top-3 right-3 text-red-400 hover:text-red-300 p-1 rounded hover:bg-red-500/10 cursor-pointer"
                    title="Eliminar paso"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                )}

                <div className="text-5xl font-black mb-4 leading-none" style={{ color: `${s.color}20` }}>
                  {isEditing ? (
                    <input
                      type="text"
                      value={s.step}
                      onChange={(e) => handleStepChange(i, 'step', e.target.value)}
                      className="w-16 bg-black/40 border border-white/20 rounded px-1 text-2xl font-black text-purple-400 focus:outline-none"
                    />
                  ) : (
                    s.step
                  )}
                </div>
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl mb-4"
                  style={{ background: `${s.color}15`, border: `1px solid ${s.color}30` }}
                >
                  <i className={`${s.icon} text-lg`} style={{ color: s.color }} />
                </div>

                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={s.title}
                      onChange={(e) => handleStepChange(i, 'title', e.target.value)}
                      className="w-full bg-black/40 border border-purple-500/40 rounded px-2.5 py-1 text-white font-bold text-sm focus:outline-none"
                    />
                    <textarea
                      value={s.desc}
                      onChange={(e) => handleStepChange(i, 'desc', e.target.value)}
                      rows={3}
                      className="w-full bg-black/40 border border-white/10 rounded px-2.5 py-1 text-white/60 text-xs focus:outline-none focus:border-purple-500"
                    />
                  </div>
                ) : (
                  <>
                    <h3 className="text-base font-bold text-white mb-2">{s.title}</h3>
                    <p className="text-white/50 text-sm leading-relaxed">{s.desc}</p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main CTA */}
      <section className="relative py-24 bg-[#030006] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(140,82,255,0.10)_0%,transparent_60%)] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center relative z-10">
          <div
            className="relative rounded-3xl overflow-hidden border border-[#8c52ff]/20 p-10 md:p-16"
            style={{ background: 'linear-gradient(135deg, rgba(140,82,255,0.08) 0%, rgba(0,194,255,0.08) 100%)' }}
          >
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8c52ff]/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00c2ff]/40 to-transparent" />

            <div className="w-16 h-16 flex items-center justify-center rounded-2xl mx-auto mb-6 bg-[#8c52ff]/15 border border-[#8c52ff]/30">
              <i className="ri-briefcase-4-line text-2xl text-[#8c52ff]" />
            </div>

            {isEditing ? (
              <div className="space-y-4 mb-8 max-w-xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
                  <div>
                    <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Título</label>
                    <input
                      type="text"
                      value={data.cta.titlePrefix}
                      onChange={(e) =>
                        onChange &&
                        onChange({ ...data, cta: { ...data.cta, titlePrefix: e.target.value } })
                      }
                      className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white font-black text-xl focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-purple-400 uppercase tracking-wider block mb-1">Título Destacado</label>
                    <input
                      type="text"
                      value={data.cta.titleHighlight}
                      onChange={(e) =>
                        onChange &&
                        onChange({ ...data, cta: { ...data.cta, titleHighlight: e.target.value } })
                      }
                      className="w-full bg-black/40 border border-purple-500/40 rounded-lg px-3 py-2 text-purple-300 font-black text-xl focus:outline-none"
                    />
                  </div>
                </div>

                <div className="text-left">
                  <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Subtítulo</label>
                  <textarea
                    value={data.cta.subtitle}
                    onChange={(e) =>
                      onChange &&
                      onChange({ ...data, cta: { ...data.cta, subtitle: e.target.value } })
                    }
                    rows={2}
                    className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-white/80 text-sm focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
                  <div className="space-y-1">
                    <label className="text-[11px] text-purple-400 uppercase tracking-wider block">Botón Principal</label>
                    <input
                      type="text"
                      value={data.cta.ctaBook}
                      onChange={(e) =>
                        onChange &&
                        onChange({ ...data, cta: { ...data.cta, ctaBook: e.target.value } })
                      }
                      className="w-full bg-black/40 border border-purple-500/40 rounded px-2.5 py-1 text-white text-xs font-semibold focus:outline-none"
                      placeholder="Texto"
                    />
                    <input
                      type="text"
                      value={effectiveBookLink}
                      onChange={(e) =>
                        onChange &&
                        onChange({ ...data, cta: { ...data.cta, ctaBookLink: e.target.value } })
                      }
                      className="w-full bg-black/40 border border-white/20 rounded px-2.5 py-1 text-white/60 text-xs focus:outline-none"
                      placeholder="Enlace URL"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] text-white/50 uppercase tracking-wider block">Botón Secundario</label>
                    <input
                      type="text"
                      value={data.cta.ctaServices}
                      onChange={(e) =>
                        onChange &&
                        onChange({ ...data, cta: { ...data.cta, ctaServices: e.target.value } })
                      }
                      className="w-full bg-black/40 border border-white/20 rounded px-2.5 py-1 text-white text-xs font-semibold focus:outline-none"
                      placeholder="Texto"
                    />
                    <input
                      type="text"
                      value={effectiveServicesLink}
                      onChange={(e) =>
                        onChange &&
                        onChange({ ...data, cta: { ...data.cta, ctaServicesLink: e.target.value } })
                      }
                      className="w-full bg-black/40 border border-white/20 rounded px-2.5 py-1 text-white/60 text-xs focus:outline-none"
                      placeholder="Enlace URL"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                  {data.cta.titlePrefix}<br />
                  <span className="bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] bg-clip-text text-transparent">
                    {data.cta.titleHighlight}
                  </span>
                </h2>
                <p className="text-white/55 text-base leading-relaxed max-w-xl mx-auto mb-8">
                  {data.cta.subtitle}
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  {effectiveBookLink.startsWith('http') ? (
                    <a
                      href={effectiveBookLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whitespace-nowrap inline-flex items-center justify-center gap-2.5 font-bold px-8 py-4 rounded-full bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white hover:opacity-90 hover:scale-105 transition-all duration-300 cursor-pointer text-base"
                    >
                      <i className="ri-calendar-check-line" />
                      {data.cta.ctaBook}
                    </a>
                  ) : (
                    <Link
                      to={effectiveBookLink}
                      className="whitespace-nowrap inline-flex items-center justify-center gap-2.5 font-bold px-8 py-4 rounded-full bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white hover:opacity-90 hover:scale-105 transition-all duration-300 cursor-pointer text-base"
                    >
                      <i className="ri-calendar-check-line" />
                      {data.cta.ctaBook}
                    </Link>
                  )}
                  {effectiveServicesLink.startsWith('http') ? (
                    <a
                      href={effectiveServicesLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-white/5 border border-white/20 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-all duration-300 cursor-pointer"
                    >
                      <i className="ri-arrow-left-line" />
                      {data.cta.ctaServices}
                    </a>
                  ) : (
                    <Link
                      to={effectiveServicesLink}
                      className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-white/5 border border-white/20 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-all duration-300 cursor-pointer"
                    >
                      <i className="ri-arrow-left-line" />
                      {data.cta.ctaServices}
                    </Link>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-[#030006] border-t border-white/5 py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-start gap-3 bg-white/3 border border-white/8 rounded-xl px-6 py-4 w-full">
            <i className="ri-information-line text-white/30 text-lg flex-shrink-0 mt-0.5" />
            <div className="text-left flex-1 min-w-0">
              {isEditing ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={data.disclaimer.title}
                    onChange={(e) =>
                      onChange &&
                      onChange({ ...data, disclaimer: { ...data.disclaimer, title: e.target.value } })
                    }
                    className="bg-black/40 border border-white/20 rounded px-2 py-0.5 text-white/70 text-xs font-semibold focus:outline-none"
                  />
                  <textarea
                    value={data.disclaimer.text}
                    onChange={(e) =>
                      onChange &&
                      onChange({ ...data, disclaimer: { ...data.disclaimer, text: e.target.value } })
                    }
                    rows={2}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-white/50 text-xs focus:outline-none"
                  />
                </div>
              ) : (
                <p className="text-white/35 text-xs leading-relaxed text-left">
                  <strong className="text-white/50 font-semibold">{data.disclaimer.title}:</strong> {data.disclaimer.text}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ConsultingCTA;
